import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller } from "react-hook-form";
import { Input, Checkbox } from "@wfp/ui";
import Select from "react-select";
import {
  getVendorCategories,
  getAllApprovedVendors,
} from "data-handler/ducks/vendors";
import { getCurrentSchool } from "data-handler/ducks/schools";
import {
  getCountryAdminLevels,
  requestCountryAdminLevels,
} from "data-handler/ducks/adminLevels";
import { useDispatch, useSelector } from "react-redux";
import "./_cash-to-school.scss";

export function vendorOptions(list) {
  return list
    .map((e) => ({
      value: e.id,
      label: e.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
// filter by selected vendor category
function filterVendorListBySelectedCategory(selectedCategory, list) {
  const categoryId =
    selectedCategory && typeof selectedCategory === "object"
      ? selectedCategory.value
      : selectedCategory;

  if (!categoryId) {
    return list;
  }

  return list.filter((item) => categoryId === item.vendor_category.id);
}

function getDefaultVendorFilterScope(currentSchool) {
  if (currentSchool?.adm_4?.id) {
    return "4";
  }
  if (currentSchool?.adm_3?.id) {
    return "3";
  }
  if (currentSchool?.adm_2?.id) {
    return "2";
  }
  if (currentSchool?.adm_1?.id) {
    return "1";
  }
  return "all";
}

function getVendorFilterScopeOptions(currentSchool, adminLevelNames) {
  const maxSchoolScope =
    Number(getDefaultVendorFilterScope(currentSchool)) || 0;
  const adm1Label = adminLevelNames[1] || "Admin1";
  const adm2Label = adminLevelNames[2] || "Admin2";
  const adm3Label = adminLevelNames[3] || "Admin3";
  const adm4Label = adminLevelNames[4] || "Admin4";

  return [
    { value: "all", label: "All areas" },
    { value: "1", label: adm1Label, isDisabled: maxSchoolScope < 1 },
    {
      value: "2",
      label: `${adm1Label}, ${adm2Label}`,
      isDisabled: maxSchoolScope < 2,
    },
    {
      value: "3",
      label: `${adm1Label}, ${adm2Label}, ${adm3Label}`,
      isDisabled: maxSchoolScope < 3,
    },
    {
      value: "4",
      label: `${adm1Label}, ${adm2Label}, ${adm3Label}, ${adm4Label}`,
      isDisabled: maxSchoolScope < 4,
    },
  ];
}

function filterVendorListBySchoolScope(currentSchool, list, scope) {
  if (scope === "all") {
    return list;
  }

  const scopeLevel = Number(scope);
  const schoolAdm = {
    1: currentSchool?.adm_1?.id,
    2: currentSchool?.adm_2?.id,
    3: currentSchool?.adm_3?.id,
    4: currentSchool?.adm_4?.id,
  };

  if (!schoolAdm[1]) {
    return list;
  }

  return list.filter((item) => {
    // Global vendors without admin areas should always remain available.
    if (!item?.adm_1?.id) {
      return true;
    }

    if (scopeLevel >= 1 && item?.adm_1?.id !== schoolAdm[1]) {
      return false;
    }
    if (scopeLevel >= 2 && item?.adm_2?.id !== schoolAdm[2]) {
      return false;
    }
    if (scopeLevel >= 3 && item?.adm_3?.id !== schoolAdm[3]) {
      return false;
    }
    if (scopeLevel >= 4 && item?.adm_4?.id !== schoolAdm[4]) {
      return false;
    }
    return true;
  });
}

const VendorEdit = ({
  control,
  watch,
  errors,
  setValue,
  getValues,
  register,
  purchaseCurrentStoreData,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [refreshKey, setRefreshKey] = useState(new Date().valueOf());
  const [vendorFilterScope, setVendorFilterScope] = useState("all");

  const vendorList = useSelector(getAllApprovedVendors);
  const vendorCategories = useSelector(getVendorCategories);
  const currentSchool = useSelector(getCurrentSchool);
  const countryAdminLevels = useSelector(getCountryAdminLevels);

  const selectedVendorCategory = watch(`vendor_category`);
  const noVendorInfo = watch("no_vendor_info");
  const vendorValue = watch("vendor");
  const didSetNoVendorDefault = useRef(false);
  const didSetInitialValues = useRef(false);

  const selectedVendorCategoryId =
    typeof selectedVendorCategory === "object" && selectedVendorCategory?.value
      ? selectedVendorCategory.value
      : selectedVendorCategory;

  const schoolId = currentSchool?.id;
  const countryId = currentSchool?.country?.id;
  const defaultVendorFilterScope = getDefaultVendorFilterScope(currentSchool);

  const adminLevelNames = countryAdminLevels.reduce((acc, levelConfig) => {
    acc[levelConfig.level] = levelConfig.name;
    return acc;
  }, {});

  const vendorFilterScopeOptions = getVendorFilterScopeOptions(
    currentSchool,
    adminLevelNames
  );
  const selectedVendorFilterScope = vendorFilterScopeOptions.find(
    (option) => option.value === vendorFilterScope
  );

  const areaScopedVendors = filterVendorListBySchoolScope(
    currentSchool,
    vendorList.results,
    vendorFilterScope
  );
  const vendorNameOptions = vendorOptions(
    filterVendorListBySelectedCategory(
      selectedVendorCategoryId,
      areaScopedVendors
    )
  );
  const vendorCategoriesOptions = vendorOptions(
    Object.values(vendorCategories.results)
  );

  const requiredLabelVendor = (
    <div
      className="wfp--form-required"
      styles={{ borderColor: " 2px solid #C5192D" }}
    >
      <FormattedMessage
        id="PurchaseEdit.vendorError"
        defaultMessage=" *Select vendor"
      />
    </div>
  );

  useEffect(() => {
    if (!purchaseCurrentStoreData || didSetInitialValues.current) {
      return;
    }

    if (
      purchaseCurrentStoreData.vendor_category &&
      typeof purchaseCurrentStoreData.vendor_category === "number"
    ) {
      const categoryOption = vendorCategoriesOptions.find(
        (opt) => opt.value === purchaseCurrentStoreData.vendor_category
      );
      if (categoryOption) {
        setValue("vendor_category", categoryOption.value);
      }
    }

    if (
      purchaseCurrentStoreData.vendor &&
      typeof purchaseCurrentStoreData.vendor === "number"
    ) {
      const vendorOption = vendorList.results.find(
        (v) => v.id === purchaseCurrentStoreData.vendor
      );
      if (vendorOption) {
        setValue("vendor", vendorOption.id);
      }
    }

    didSetInitialValues.current = true;
  }, [purchaseCurrentStoreData, setValue, vendorCategoriesOptions, vendorList]);

  useEffect(() => {
    setVendorFilterScope(defaultVendorFilterScope);
  }, [schoolId, defaultVendorFilterScope]);

  useEffect(() => {
    if (countryId) {
      dispatch(requestCountryAdminLevels(countryId));
    }
  }, [dispatch, countryId]);

  useEffect(() => {
    setRefreshKey(new Date().valueOf());
    setValue(`vendor`, null);
  }, [selectedVendorCategory, setValue, getValues]);

  useEffect(() => {
    // Disable vendor field when user has no vendor information
    if (!purchaseCurrentStoreData || didSetNoVendorDefault.current) {
      return;
    }

    if (!vendorValue) {
      setValue("no_vendor_info", true);
      didSetNoVendorDefault.current = true;
    } else {
      didSetNoVendorDefault.current = true;
    }
  }, [purchaseCurrentStoreData, vendorValue, setValue]);

  useEffect(() => {
    if (noVendorInfo) {
      setValue("vendor", null, { shouldDirty: true });
    }
  }, [noVendorInfo, setValue]);

  return (
    <div className="vendorContainer">
      <div className="container">
        <Controller
          render={({ onChange, value }) => {
            const selectValue =
              typeof value === "number"
                ? vendorCategoriesOptions.find((opt) => opt.value === value)
                : value;

            return (
              <div className="commodity-select__input">
                <Input
                  labelText={
                    <FormattedMessage
                      id="PurchaseEdit.vendorType"
                      defaultMessage="Vendor type"
                    />
                  }
                >
                  {() => (
                    <Select
                      options={vendorCategoriesOptions}
                      className="wfp--react-select-container"
                      classNamePrefix="wfp--react-select"
                      value={selectValue}
                      onChange={(e) => {
                        onChange(e.value);
                      }}
                      rules={{
                        required: intl.formatMessage({
                          id: "PurchaseEdit.vendorError",
                          defaultMessage: "*Select vendor",
                        }),
                      }}
                      invalid={errors[`vendor_category`]}
                      invalidText={errors[`vendor_category`]?.message}
                    />
                  )}
                </Input>{" "}
              </div>
            );
          }}
          name={`vendor_category`}
          placeholder="Select ..."
          control={control}
        />
      </div>{" "}
      {errors[`vendor_category`] && requiredLabelVendor}
      <div className="container">
        <div className={"container vendor-field"}>
          <Controller
            key={refreshKey}
            render={({ onChange, value }) => {
              const selectValue =
                typeof value === "number"
                  ? vendorNameOptions.find((opt) => opt.value === value)
                  : value;

              return (
                <Input
                  labelText={
                    <FormattedMessage
                      id="PurchaseEdit.VendorNameCreated"
                      defaultMessage="Vendor name"
                    />
                  }
                >
                  {() => (
                    <div className="vendor-select">
                      <Select
                        className="wfp--react-select-container"
                        options={vendorNameOptions}
                        control={control}
                        onChange={(e) => {
                          onChange(e.value);
                        }}
                        rules={{
                          required: !noVendorInfo
                            ? "Select vendor name"
                            : false,
                        }}
                        value={selectValue}
                        isDisabled={!!noVendorInfo}
                      />
                    </div>
                  )}
                </Input>
              );
            }}
            name={`vendor`}
            invalid={errors[`vendor`]}
            invalidText={errors[`vendor`]?.message}
          />
        </div>
      </div>
      <div className="container">
        <div className="vendor-checkbox-row">
          <div className="vendor-scope-row">
            <label className="wfp--label">
              <FormattedMessage
                id="PurchaseEdit.vendorFilterScope"
                defaultMessage="Vendor filter scope"
              />
            </label>
            <Select
              options={vendorFilterScopeOptions}
              className="wfp--react-select-container vendor-scope-select"
              classNamePrefix="wfp--react-select"
              value={selectedVendorFilterScope}
              isClearable={false}
              isDisabled={!!noVendorInfo}
              onChange={(e) => {
                setVendorFilterScope(e.value);
                setValue("vendor", null, { shouldDirty: true });
                setRefreshKey(new Date().valueOf());
              }}
            />
          </div>
        </div>
      </div>
      <div className="container">
        <div className="vendor-checkbox-row">
          <div className="checkboxVendor">
            <label className="wfp--label">
              <FormattedMessage
                id="PurchaseEdit.noAdditionalVendorInfo"
                defaultMessage="I do not have additional vendor information"
              />{" "}
            </label>
            <Controller
              render={({ onChange, value }) => (
                <Checkbox
                  className="checkbox"
                  name="no_vendor_info_checkbox"
                  id="no_vendor_info"
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onChange(checked);
                    if (checked) {
                      setValue("vendor", null, { shouldDirty: true });
                    }
                    setRefreshKey(new Date().valueOf());
                  }}
                  checked={value || false}
                />
              )}
              id="no_vendor_info"
              name={`no_vendor_info`}
              control={control}
              defaultValue={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorEdit;
